/**
 * Character-lab shaders (TGSL). The base model's painted texture is lit like a manuscript miniature: faceted or
 * smooth normals, three tempera value masses, dry pigment and directional pen work anchored to the rest mesh.
 * Coat recolouring stays independent. The outline is an inverted hull, not a post-process around the page.
 */
import tgpu from 'typegpu';
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';
import { faceEraseMask, faceFeatures, faceLayout } from './faceShader';

export const Frame = d.struct({
  viewProj: d.mat4x4f,
  model: d.mat4x4f,
  eye: d.vec4f,
  /** xyz key light, w expanded rehearsal floor (standalone supers). */
  light: d.vec4f,
  /** x time, y outline width (px), z facet 0..1, w recolour amount 0..1 */
  params: d.vec4f,
  /** x w, y h, z 1/w, w 1/h */
  viewport: d.vec4f,
  coatDark: d.vec4f,
  coatMid: d.vec4f,
  coatLight: d.vec4f,
  /** x hatch strength, y band softness, z painted-page profile (1; legacy <= .2), w grain strength */
  style: d.vec4f,
  /** x debug mode (0 off, 1 bone owners), y highlighted bone */
  debug: d.vec4f,
  /** x pattern; y chest center x; z chest height; w torso radius. Pigments remain in rest space. */
  coatPattern: d.vec4f,
  /** Seed offset and independent pattern density. */
  coatVariation: d.vec4f,
});

export const MAX_BONES = 64;
export const skinLayout = tgpu.bindGroupLayout({
  bones: { storage: d.arrayOf(d.mat4x4f, MAX_BONES), access: 'readonly' },
});

export const frameLayout = tgpu.bindGroupLayout({ frame: { uniform: Frame } });
/** Same trajectory ribbon as combat, projected through the atelier orbit camera. */
export const atelierTrailVertex=tgpu.vertexFn({in:{posSize:d.vec4f,color:d.vec4f,params:d.vec4f},out:{position:d.builtin.position,color:d.vec4f,uv:d.vec2f}})((v)=>{
  'use gpu';
  return {position:frameLayout.$.frame.viewProj.mul(d.vec4f(v.posSize.xyz,1)),color:v.color,uv:v.params.xy};
});
export const texLayout = tgpu.bindGroupLayout({
  albedo: { texture: d.texture2d(d.f32) },
  samp: { sampler: 'filtering' },
});

/** aux: body = face space (u, v, frontness, depth); cloth = rest position + boundary distance. */
export const MeshVertex = d.unstruct({ position: d.float32x3, normal: d.float32x3, uv: d.float32x2, joints: d.uint8x4, weights: d.unorm8x4, aux: d.float32x4, repair: d.float32x3 });

/** Linear blend skinning of a point (w = 1) or direction (w = 0). */
const skinned = (p: d.v4f, j: d.v4u, w: d.v4f): d.v4f => {
  'use gpu';
  const b = skinLayout.$.bones;
  return b[j.x].mul(p).mul(w.x).add(b[j.y].mul(p).mul(w.y)).add(b[j.z].mul(p).mul(w.z)).add(b[j.w].mul(p).mul(w.w));
};
export const meshLayout = tgpu.vertexLayout((n: number) => d.disarrayOf(MeshVertex, n));

const hash21 = (p: d.v2f): number => {
  'use gpu';
  const q = std.fract(p.mul(d.vec2f(123.34, 456.21)));
  const r = q.add(std.dot(q, q.add(45.32)));
  return std.fract(r.x * r.y);
};

/** Low-frequency pigment variation, fixed in face space rather than on screen. */
const facePigment = (p: d.v2f): number => {
  'use gpu';
  const cell = std.floor(p);
  const f = std.fract(p);
  const u = f.mul(f).mul(d.vec2f(3).sub(f.mul(2)));
  return std.mix(std.mix(hash21(cell), hash21(cell.add(d.vec2f(1, 0))), u.x), std.mix(hash21(cell.add(d.vec2f(0, 1))), hash21(cell.add(d.vec2f(1, 1))), u.x), u.y);
};

const toLinear = (c: d.v3f): d.v3f => {
  'use gpu';
  return std.pow(c, d.vec3f(2.2));
};
const toGamma = (c: d.v3f): d.v3f => {
  'use gpu';
  return std.pow(std.clamp(c, d.vec3f(0), d.vec3f(1)), d.vec3f(1 / 2.2));
};

/** Standalone canvas consumes gamma colour; the game composite consumes linear colour. */
const outputColor = (color: d.v4f): d.v4f => {
  'use gpu';
  return d.vec4f(std.select(color.xyz, toLinear(color.xyz), frameLayout.$.frame.debug.w > 0.5), color.w);
};

// ── background: lapis vault with gold stars, warm parchment floor glow ───────────────────────────────────────────
export const bgVertex = tgpu.vertexFn({
  in: { vi: d.builtin.vertexIndex },
  out: { position: d.builtin.position, uv: d.vec2f },
})((input) => {
  'use gpu';
  const pts = [d.vec2f(-1, -1), d.vec2f(3, -1), d.vec2f(-1, 3)];
  const p = pts[input.vi];
  return { position: d.vec4f(p, 0.999, 1), uv: d.vec2f(p.x * 0.5 + 0.5, 0.5 - p.y * 0.5) };
});

export const bgFragment = tgpu.fragmentFn({
  in: { uv: d.vec2f, position: d.builtin.position },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const aspect = f.viewport.x * f.viewport.w;
  const uv = input.uv;
  // lapis gradient, lighter toward the top centre
  const top = d.vec3f(0.15, 0.27, 0.31);
  const bottom = d.vec3f(0.045, 0.095, 0.12);
  let col = std.mix(bottom, top, std.smoothstep(1.0, 0.0, uv.y));
  const c = d.vec2f((uv.x - 0.5) * aspect, uv.y - 0.35);
  col = col.mul(1.0 - 0.35 * std.smoothstep(0.3, 1.2, std.length(c)));
  // gold stars on a jittered grid (four-point shape)
  const grid = d.vec2f(uv.x * aspect, uv.y).mul(9.0);
  const cell = std.floor(grid);
  const h = hash21(cell);
  const jitter = d.vec2f(hash21(cell.add(7.1)), hash21(cell.add(3.7))).mul(0.6).add(0.2);
  const q = grid.sub(cell).sub(jitter);
  const r = std.abs(q.x) * std.abs(q.y) * 60.0 + std.length(q) * 3.0;
  const size = std.select(d.f32(0), 0.28 + 0.25 * hash21(cell.add(1.3)), h > 0.76);
  const star = std.clamp(size * (1.0 - r * 1.8), 0.0, 1.0);
  const twinkle = 0.75 + 0.25 * std.sin(f.params.x * 1.5 + h * 40.0);
  col = std.mix(col, d.vec3f(0.95, 0.76, 0.33), star * twinkle);
  // parchment grain
  const g = hash21(input.position.xy.add(std.floor(f.params.x * 0.0)));
  col = col.mul(1.0 - f.style.w * 0.5 + f.style.w * g);
  return d.vec4f(col, 1);
});

// ── floor disc with a soft contact shadow ───────────────────────────────────────────────────────────────────────
export const floorVertex = tgpu.vertexFn({
  in: { vi: d.builtin.vertexIndex },
  out: { position: d.builtin.position, local: d.vec2f },
})((input) => {
  'use gpu';
  const pts = [d.vec2f(-1, -1), d.vec2f(1, -1), d.vec2f(1, 1), d.vec2f(-1, -1), d.vec2f(1, 1), d.vec2f(-1, 1)];
  const p = pts[input.vi];
  const radius=std.mix(.55,8.0,frameLayout.$.frame.light.w);
  const world = d.vec4f(p.x * radius, 0.0, p.y * radius, 1);
  return { position: frameLayout.$.frame.viewProj.mul(world), local: p };
});

export const floorFragment = tgpu.fragmentFn({
  in: { local: d.vec2f, position: d.builtin.position },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const r = std.length(input.local);
  if (r > 1.0) {
    std.discard();
  }
  const parchment = d.vec3f(0.93, 0.86, 0.7);
  const shadow = std.smoothstep(0.55, 0.0, std.length(input.local.mul(d.vec2f(1.0, 1.5))))*(1-f.light.w);
  let col = parchment.mul(1.0 - 0.45 * shadow);
  // inked rim
  col = std.mix(col, d.vec3f(0.17, 0.11, 0.08), std.smoothstep(0.95, 0.985, r) * (1.0 - std.smoothstep(0.985, 1.0, r)));
  col = std.mix(col, d.vec3f(0.62, 0.48, 0.18), std.smoothstep(0.9, 0.93, r) * (1.0 - std.smoothstep(0.93, 0.95, r)) * 0.8);
  const g = hash21(input.position.xy);
  col = col.mul(1.0 - f.style.w * 0.5 + f.style.w * g);
  return d.vec4f(col, 1);
});

/** A broken pen stroke. Filter the unwrapped phase before periodic wrapping;
 * unresolved strokes disappear instead of sparkling as a character walks. */
const penStroke = (phase: number, along: number): number => {
  'use gpu';
  const footprint = std.fwidth(phase);
  const aa = std.max(0.018, footprint * 0.55);
  const distance = std.abs(std.fract(phase) - 0.5);
  const line = 1.0 - std.smoothstep(0.065 - aa, 0.065 + aa, distance);
  const broken = std.smoothstep(0.37, 0.57, facePigment(d.vec2f(std.floor(phase), along)));
  return line * broken * (1.0 - std.smoothstep(0.55, 1.15, footprint));
};

/** Separate tapered fur ticks, staggered and tilted per cell. They cannot join
 * into the long parallel scratches produced by a continuous stripe field. */
const furPen = (p: d.v2f): number => {
  'use gpu';
  const grid = p.mul(d.vec2f(166.0, 143.0));
  const footprint = std.length(std.fwidth(grid));
  const staggered = d.vec2f(grid.x + std.floor(grid.y) * 0.43, grid.y);
  const cell = std.floor(staggered);
  const q = std.fract(staggered).sub(0.5);
  const seed = hash21(cell);
  const halfLength = 0.19 + hash21(cell.add(17.1)) * 0.12;
  const along = std.abs(q.y);
  const taper = (1.0 - std.smoothstep(0.06, halfLength, along)) * 0.07;
  const tilt = (seed - 0.5) * 0.6 + std.clamp(p.x * 1.3, -0.3, 0.3);
  const across = std.abs(q.x + q.y * tilt + (hash21(cell.add(9.3)) - 0.5) * 0.32);
  const aa = std.max(0.025, footprint * 0.45);
  const tick = (1.0 - std.smoothstep(taper - aa, taper + aa, across)) * (1.0 - std.smoothstep(halfLength - aa, halfLength + aa, along));
  return tick * (1.0 - std.smoothstep(0.65, 1.25, footprint));
};

/** Sparse mid-scale locks remain legible on a 240 px figure. The centre of
 * each tapered mark is jittered independently, so they cannot form rows or
 * continuous scratches. Face-space spread splays cheek fur away from the nose;
 * every coordinate is unposed, including the direction of the mark. */
const furLocks = (p: d.v2f, spread: number): number => {
  'use gpu';
  const grid = p.mul(d.vec2f(43.0, 34.0));
  const footprint = std.length(std.fwidth(grid));
  const staggered = d.vec2f(grid.x + std.floor(grid.y) * 0.47, grid.y);
  const cell = std.floor(staggered);
  const seed = hash21(cell.add(31.7));
  const q = std.fract(staggered).sub(d.vec2f(0.5 + (seed - 0.5) * 0.3, 0.5 + (hash21(cell.add(6.3)) - 0.5) * 0.18));
  const halfLength = 0.19 + hash21(cell.add(19.4)) * 0.1;
  const along = std.abs(q.y);
  const taper = (1.0 - std.smoothstep(-halfLength, halfLength, q.y)) * 0.07;
  const tilt = spread * 0.72 + (seed - 0.5) * 0.4;
  const across = std.abs(q.x + q.y * tilt + q.y * q.y * 0.18);
  const aa = std.max(0.025, footprint * 0.38);
  const mark = (1.0 - std.smoothstep(taper - aa, taper + aa, across)) * (1.0 - std.smoothstep(halfLength - aa, halfLength + aa, along));
  return mark * std.smoothstep(0.2, 0.44, seed) * (1.0 - std.smoothstep(0.8, 1.65, footprint));
};

/** Egg-tempera on the figure itself. `rest` is never world, screen or time:
 * the wash and pen marks travel with skinning and survive a camera change. */
const stylize = (albedo: d.v3f, world: d.v3f, normalIn: d.v3f, rest: d.v3f, metal: number): d.v4f => {
  'use gpu';
  const f = frameLayout.$.frame;
  const v = std.normalize(f.eye.xyz.sub(world));
  const painted = std.smoothstep(0.75, 1.0, f.style.z);
  // facet normal from screen derivatives; WebGPU's framebuffer y points down, so orient it toward the viewer
  const rawFace = std.normalize(std.cross(std.dpdx(world), std.dpdy(world)));
  const faceN = std.select(rawFace, std.neg(rawFace), std.dot(rawFace, v) < 0.0);
  const smoothN = std.normalize(normalIn);
  const n = std.normalize(std.mix(smoothN, faceN, f.params.z));
  const l = std.normalize(f.light.xyz);
  const ndl = std.dot(n, l);
  // Broad painted value masses, not a glossy continuous diffuse gradient.
  const p = d.vec2f(rest.x + rest.z * 0.63, rest.y - rest.z * 0.21);
  const wash = facePigment(p.mul(19.0));
  const soft = std.mix(0.045 + f.style.y * 0.3, 0.028, painted);
  const edge = (wash - 0.5) * 0.08;
  const t1 = std.smoothstep(-0.15 - soft + edge, -0.15 + soft + edge, ndl);
  const t2 = std.smoothstep(0.45 - soft + edge, 0.45 + soft + edge, ndl);
  const shadowTint = std.mix(d.vec3f(0.52, 0.46, 0.39), d.vec3f(0.43, 0.375, 0.30), painted);
  const midTint = d.vec3f(0.98, 0.94, 0.86);
  let light = std.mix(shadowTint, midTint, t1);
  light = std.mix(light, std.mix(d.vec3f(1.1, 1.04, 0.92), d.vec3f(1.015, 0.975, 0.88), painted), t2);
  let col = albedo.mul(light);
  // Slightly wandering, interrupted strokes follow the unposed material.
  const bend = facePigment(p.mul(11.0)) * 0.42;
  const h1 = penStroke(p.x * 91.0 + p.y * 34.0 + bend, p.y * 74.0);
  const h2 = penStroke(p.x * 71.0 - p.y * 76.0 - bend, p.x * 67.0);
  const shade = 1.0 - std.smoothstep(0.1, 0.52, ndl);
  const hatch = std.clamp(h1 * shade * 0.72 + h2 * (1.0 - t1) * 0.45, 0.0, 1.0) * f.style.x;
  col = std.mix(col, col.mul(d.vec3f(0.34, 0.29, 0.24)), hatch);
  // Page-sized figures need a few resolved pen marks, not a dense scratch
  // field. Only the shadow turns carry these staggered, broken cross strokes.
  // The rest-space field does not slide over the surface during a walk.
  const pageHatch = penStroke(p.x * 47.0 + p.y * 18.0 + bend, p.y * 31.0);
  const pageCross = penStroke(p.x * 36.0 - p.y * 49.0 - bend, p.x * 29.0);
  const pageShade = 1.0 - std.smoothstep(0.25, 0.76, ndl);
  const pageInk = (pageHatch * pageShade * 0.68 + pageCross * (1.0 - t1) * 0.32) * painted;
  col = std.mix(col, col.mul(d.vec3f(0.24, 0.18, 0.13)), pageInk);
  // Metal is opaque painted tin/gold: one broad chipped highlight, no moving
  // half-vector specular spot and no luminous silhouette around black fur.
  const metalLight = std.smoothstep(0.56, 0.65, ndl + edge);
  col = std.mix(col, col.mul(0.72), metal * (1.0 - t2) * 0.35);
  col = std.mix(col, col.mul(1.16).add(d.vec3f(0.055, 0.047, 0.032)), metal * metalLight * 0.65);
  let outc = toGamma(col);
  const fineScale = p.mul(210.0);
  const fineVisibility = 1.0 - std.smoothstep(0.6, 1.8, std.length(std.fwidth(fineScale)));
  const granules = (facePigment(fineScale) - 0.5) * fineVisibility;
  const pigmentAmount = std.clamp(f.style.w / 0.028, 0.0, 2.0);
  outc = outc.mul(1.0 + ((wash - 0.5) * 0.09 + granules * 0.1) * pigmentAmount);
  const dry = std.smoothstep(0.65, 0.86, facePigment(p.mul(43.0))) * 0.11 * pigmentAmount;
  outc = std.mix(outc, outc.mul(1.2).add(d.vec3f(0.045, 0.037, 0.025)), dry);
  // A thin interior ink return follows the view-facing turn of the actual
  // surface. Its width is derivative filtered; unlike a dark diffuse rim it
  // does not become a broad charcoal stripe on a small arm or cheek.
  const facing = std.abs(std.dot(n, v));
  const edgeAA = std.max(0.012, std.fwidth(facing));
  const edgeWidth = std.clamp(edgeAA * 0.7, 0.018, 0.12);
  const turnInk = 1.0 - std.smoothstep(edgeWidth, edgeWidth + edgeAA, facing);
  const inkColor = outc.mul(0.22).add(d.vec3f(0.038, 0.025, 0.014));
  outc = std.mix(outc, inkColor, turnInk * 0.64 * painted);
  outc = std.mix(outc, d.vec3f(1.0, 0.87, 0.62), std.clamp(f.debug.z, 0.0, 0.5));
  return d.vec4f(outc, 1);
};

// ── character ──────────────────────────────────────────────────────────────────────────────────────────────────
export const charVertex = tgpu.vertexFn({
  in: { position: d.vec3f, normal: d.vec3f, uv: d.vec2f, joints: d.vec4u, weights: d.vec4f, aux: d.vec4f, repair: d.vec3f },
  out: { position: d.builtin.position, world: d.vec3f, normal: d.vec3f, uv: d.vec2f, owner: d.interpolate('flat', d.u32), face: d.vec4f, repair: d.vec3f, rest: d.vec3f },
})((input) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const local = skinned(d.vec4f(input.position, 1), input.joints, input.weights);
  const world = f.model.mul(d.vec4f(local.xyz, 1));
  const ln = skinned(d.vec4f(input.normal, 0), input.joints, input.weights);
  const n = std.normalize(f.model.mul(d.vec4f(ln.xyz, 0)).xyz);
  return { position: f.viewProj.mul(world), world: world.xyz, normal: n, uv: input.uv, owner: input.joints.x, face: input.aux, repair: input.repair, rest:input.position };
});

export const charFragment = tgpu.fragmentFn({
  in: { world: d.vec3f, normal: d.vec3f, uv: d.vec2f, owner: d.interpolate('flat', d.u32), face: d.vec4f, repair: d.vec3f, rest:d.vec3f, position: d.builtin.position },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const painted = std.smoothstep(0.75, 1.0, f.style.z);
  let tex = std.textureSample(texLayout.$.albedo, texLayout.$.samp, input.uv);
  const repaired = std.textureSampleLevel(texLayout.$.albedo, texLayout.$.samp, input.repair.xy, 2.0);
  tex = std.mix(tex, repaired, std.clamp(input.repair.z, 0.0, 1.0));
  const fp = faceLayout.$.face;
  const onFace = fp.misc.z > 0.5 && input.face.z > -0.05 && input.face.w > -1.6;
  const fuv = input.face.xy;
  // Imported coats contain high-contrast painted triangle edges. Keep their
  // broad markings, but replace that baked tessellation with a soft pigment
  // field before adding rest-space pen work. Never smear the sculpted nose,
  // teeth or detached whiskers; those are intentionally fine source details.
  const textureSize = d.vec2f(std.textureDimensions(texLayout.$.albedo, 0));
  const texelFootprint = std.max(std.length(std.dpdx(input.uv).mul(textureSize)), std.length(std.dpdy(input.uv).mul(textureSize)));
  const coatLod = std.max(std.mix(4.1, 3.2, painted), std.log2(std.max(1.0, texelFootprint)));
  const coatField = std.textureSampleLevel(texLayout.$.albedo, texLayout.$.samp, input.uv, coatLod);
  const repairField = std.textureSampleLevel(texLayout.$.albedo, texLayout.$.samp, input.repair.xy, coatLod);
  const softCoat = std.mix(coatField, repairField, std.clamp(input.repair.z, 0.0, 1.0));
  const muzzleDetail = (1.0 - std.smoothstep(0.28, 0.46, std.abs(fuv.x))) * (1.0 - std.smoothstep(-0.35, -0.18, fuv.y));
  const keepDetail = std.select(d.f32(0), muzzleDetail, onFace);
  const whisker = input.face.z < -1.5 && input.face.w > -8.5 && input.face.w < -7.5;
  tex = std.mix(tex, softCoat, std.select((1.0 - keepDetail) * std.mix(0.84, 0.58, painted), d.f32(0), whisker));
  if (onFace) {
    // Erase the source eyes with fur from the cheek and the lower muzzle.
    const cheek = std.textureSampleLevel(texLayout.$.albedo, texLayout.$.samp, fp.skinUv.xy, 3.0).xyz;
    const muzzle = std.textureSampleLevel(texLayout.$.albedo, texLayout.$.samp, fp.skinUv.zw, 3.0).xyz;
    // Socket walls have overlapping UV islands, so even a blurred source sample
    // can reintroduce triangular painted fragments. Use one continuous field in
    // head space, with the animal's own upper-fur and lower-muzzle colours.
    const furBlend = std.select(std.smoothstep(-0.95, 0.2, fuv.y), std.smoothstep(-0.38, 0.22, fuv.y), fp.erase.z > 0.5);
    const clean = std.mix(muzzle, cheek, furBlend);
    const pigment = 0.92 + facePigment(fuv.mul(d.vec2f(19, 26))) * 0.07 + facePigment(fuv.mul(d.vec2f(85, 145))) * 0.035;
    const skin = clean.mul(pigment);
    tex = d.vec4f(std.mix(tex.xyz, skin, faceEraseMask(fuv) * std.smoothstep(-0.05, 0.12, input.face.z)), 1);
  }
  if (f.debug.x > 0.5) {
    const k = d.f32(input.owner);
    const hue = d.vec3f(std.fract(k * 0.618), std.fract(k * 0.618 + 0.33), std.fract(k * 0.618 + 0.67));
    const c = std.clamp(std.abs(hue.mul(6.0).sub(3.0)).sub(1.0), d.vec3f(0), d.vec3f(1));
    const hl = std.select(d.f32(0.25), d.f32(1), std.abs(k - f.debug.y) < 0.5);
    tex = d.vec4f(std.mix(tex.xyz, c, 0.75 * hl + 0.15), 1);
  }
  let albedo = toLinear(tex.xyz);
  // procedural coat recolour: gradient map on the painted luminance
  const lum = std.dot(tex.xyz, d.vec3f(0.299, 0.587, 0.114));
  const lo = std.mix(f.coatDark.xyz, f.coatMid.xyz, std.smoothstep(0.08, 0.5, lum));
  const mapped = std.mix(lo, f.coatLight.xyz, std.smoothstep(0.45, 0.9, lum));
  albedo = std.mix(albedo, toLinear(mapped), f.params.w);
  if(f.coatPattern.x>0.5){
    const p=input.rest.mul(f.coatVariation.y).add(d.vec3f(f.coatVariation.x*.013,0,f.coatVariation.x*.027));
    const fiber=facePigment(d.vec2f(p.x+p.z*.7,p.y).mul(d.vec2f(650,190)));
    const grain=0.94+0.06*fiber;
    let fur=f.coatMid.xyz;
    // An irregular continuous stripe field follows the original mesh, including tail and limbs.
    const stripe=std.sin(p.y*88+std.abs(p.x-f.coatPattern.y)*39+std.sin(p.z*27+p.x*18)*1.9+facePigment(p.xy.mul(27))*.9);
    let stripeMask=std.smoothstep(.45,.73,stripe+fiber*.18);
    if(onFace){
      // Forehead forks and cheek ticks follow the face, rather than wrapping
      // the entire head in the body's horizontal banding.
      const ax=std.abs(fuv.x);
      const fork=std.sin(ax*16-fuv.y*2.2);
      const forehead=std.smoothstep(.65,.88,fork)*std.smoothstep(.24,.52,fuv.y)*(1-std.smoothstep(.82,1.2,ax));
      const cheek=std.smoothstep(.6,.88,std.sin(fuv.y*20+ax*5))*std.smoothstep(.62,.92,ax)*(1-std.smoothstep(.06,.3,fuv.y))*std.smoothstep(-.9,-.5,fuv.y);
      stripeMask=std.max(forehead,cheek);
    }
    if(f.coatPattern.x>2.5&&f.coatPattern.x<3.5)fur=std.mix(fur,f.coatDark.xyz,stripeMask*.73);
    if(f.coatPattern.x>3.5&&f.coatPattern.x<4.5){
      const patch=facePigment(d.vec2f(p.x+p.z*.57,p.y-p.z*.29).mul(10.5));
      fur=std.mix(f.coatLight.xyz,f.coatMid.xyz,std.smoothstep(.29,.39,patch));
      fur=std.mix(fur,f.coatDark.xyz,std.smoothstep(.57,.67,patch));
    }
    if(f.coatPattern.x>4.5&&f.coatPattern.x<5.5){
      const field=d.vec2f(p.x+p.z*.61,p.y).mul(21);
      const cell=field.add(d.vec2f(facePigment(field.mul(.35)),facePigment(field.mul(.41).add(17))).mul(.5));
      const tile=std.floor(cell);
      const jitter=hash21(tile);
      const q=std.fract(cell).sub(d.vec2f(.36+jitter*.26,.35+hash21(tile.add(6))*.3));
      const radius=std.length(q.mul(d.vec2f(1.1+jitter*.3,.9+hash21(tile.add(2))*.4)))+facePigment(cell.mul(4))*.09;
      const ring=std.smoothstep(.1,.17,radius)*(1-std.smoothstep(.25,.33,radius));
      const broken=std.smoothstep(.2,.4,facePigment(cell.mul(3).add(9)));
      fur=std.mix(fur,f.coatLight.xyz,(1-std.smoothstep(.11,.18,radius))*.2);
      fur=std.mix(fur,f.coatDark.xyz,ring*(.7+jitter*.25)*broken);
    }
    if(f.coatPattern.x>5.5&&f.coatPattern.x<6.5){
      const marble=std.sin((p.x+p.z*.63)*43+facePigment(p.xy.mul(8))*8+p.y*13);
      fur=std.mix(fur,f.coatDark.xyz,std.smoothstep(.1,.55,marble)*.85);
    }
    if(f.coatPattern.x>6.5&&f.coatPattern.x<7.5){
      const extremities=std.max(1-std.smoothstep(.09,.24,input.rest.y),std.smoothstep(.86,.99,input.rest.y));
      const mask=std.select(d.f32(0),(1-std.smoothstep(.5,1.0,std.length(fuv.mul(d.vec2f(.8,1.1)))))*.88,onFace);
      fur=std.mix(f.coatLight.xyz,f.coatMid.xyz,.25);
      fur=std.mix(fur,f.coatDark.xyz,std.max(mask,extremities));
    }
    if(f.coatPattern.x>7.5&&f.coatPattern.x<8.5){
      const saddle=(1-std.smoothstep(-.06,.055,input.rest.z))*std.smoothstep(.22,.43,input.rest.y)*(1-std.smoothstep(.68,.83,input.rest.y));
      fur=std.mix(fur,f.coatDark.xyz,saddle*.9);
    }
    if(f.coatPattern.x>8.5){
      const spots=facePigment(d.vec2f(p.x+p.z*.6,p.y).mul(70));
      fur=std.mix(fur,f.coatDark.xyz,std.smoothstep(.65,.78,spots)*.82);
    }
    const front=std.smoothstep(-.035,.035,input.rest.z);
    const bib=(1-std.smoothstep(f.coatPattern.w*.65,f.coatPattern.w*1.05,std.abs(input.rest.x-f.coatPattern.y)))*front*(1-std.smoothstep(f.coatPattern.z+.04,f.coatPattern.z+.1,input.rest.y));
    const socks=1-std.smoothstep(.085,.13,input.rest.y);
    const faceBib=(1-std.smoothstep(-.4,-.05,fuv.y))*(1-std.smoothstep(.48,.8,std.abs(fuv.x)))*std.smoothstep(0,.2,input.face.z);
    const muzzleMask=std.select(d.f32(0),faceBib,onFace);
    const white=std.max(muzzleMask,std.max(bib,socks));
    if(f.coatPattern.x>1.5&&(f.coatPattern.x<6.5||f.coatPattern.x>7.5))fur=std.mix(fur,f.coatLight.xyz,white);
    else fur=std.mix(fur,f.coatLight.xyz,muzzleMask*.55);
    // Keep the sculpted nose/mouth ink after replacing the source coat pattern.
    const nose=(1-std.smoothstep(.13,.24,std.abs(fuv.x)))*std.smoothstep(-1,-.8,fuv.y)*(1-std.smoothstep(-.38,-.22,fuv.y));
    const ink=std.select(d.f32(0),nose*(1-std.smoothstep(.12,.3,lum)),onFace&&fp.erase.z>.5);
    fur=std.mix(fur,tex.xyz,ink);
    albedo=toLinear(fur.mul(grain));
  }

  // Short fur pen marks replace the source triangle seams with a material
  // direction. Their broken tips stay attached to the rest coat, and fade
  // below one pixel; the separate eye/mouth layer remains untouched.
  const furP = d.vec2f(input.rest.x + input.rest.z * 0.61, input.rest.y - input.rest.z * 0.16);
  const furStroke = furPen(furP);
  const furSpread = std.select(std.clamp(input.rest.x * 4.0, -0.8, 0.8), std.clamp(fuv.x * 1.4, -1.0, 1.0), onFace);
  const furLock = furLocks(furP, furSpread);
  // A separate, sparse pen scale reads as a 2–4 px lock on the small traveller.
  // Magnifying the whole fine field would make a hairy parallel scratch grid;
  // these remain isolated tapered marks with independent rest-space jitter.
  const pageLock = furLocks(furP.mul(0.62).add(d.vec2f(0.137, 0.219)), furSpread);
  const furTurn = 1.0 - std.smoothstep(0.05, 0.8, std.dot(std.normalize(input.normal), std.normalize(f.light.xyz)));
  const furGate = std.select(1.0 - keepDetail * 0.7, d.f32(0), whisker);
  const furInk = std.clamp(furStroke * (0.11 + furTurn * 0.1) + furLock * (0.43 + furTurn * 0.3) + pageLock * (0.64 + furTurn * 0.28) * painted, 0.0, 0.86) * furGate;
  albedo = std.mix(albedo, albedo.mul(d.vec3f(0.48, 0.42, 0.36)), furInk);
  let outc = stylize(albedo, input.world, input.normal, input.rest, d.f32(0));
  if (onFace) {
    // The reconstructed face is a painted curved surface. Analytic normals keep
    // leftover imported socket topology from making isolated dark facets in it.
    const faceNormal = std.normalize(d.vec3f(fuv.x * 0.85, (fuv.y + 0.15) * 0.75, 1));
    const faceDot = std.dot(faceNormal, std.normalize(f.light.xyz));
    const faceLight = std.smoothstep(-0.15, 0.8, faceDot);
    const continuousTint = std.mix(d.vec3f(0.52, 0.46, 0.39), d.vec3f(1.1, 1.04, 0.92), faceLight);
    // Keep the repaired analytic surface, but do not paste smooth rounded
    // illumination back over the printed body. Three restrained pigment
    // planes follow the same face coordinates as its skin; no source socket
    // normals, eye masks, colors or facial proportions are changed.
    const paintedFace = std.smoothstep(.75,1.0,f.style.z);
    const faceEdge = (facePigment(fuv.mul(d.vec2f(9,13)))-.5)*.085;
    const faceMid = std.smoothstep(.217+faceEdge,.263+faceEdge,faceDot);
    const faceTop = std.smoothstep(.627+faceEdge,.673+faceEdge,faceDot);
    let facePlanes = std.mix(d.vec3f(.62,.56,.46),d.vec3f(1.0,.95,.86),faceMid);
    facePlanes = std.mix(facePlanes,d.vec3f(1.07,1.02,.92),faceTop);
    const tint = std.mix(continuousTint,facePlanes,paintedFace);
    const faceColor = toGamma(albedo.mul(tint));
    const cleanArea = faceEraseMask(fuv) * std.smoothstep(-0.05, 0.15, input.face.z);
    const socket = 1.0 - std.smoothstep(0.22, 0.55, std.length(d.vec2f(std.abs(fuv.x) - 0.5, fuv.y + 0.18)));
    outc = d.vec4f(std.mix(outc.xyz, faceColor, cleanArea * (0.18 + socket * 0.42)), 1);
  }
  // evaluated unconditionally: derivatives (fwidth) need uniform control flow
  const feat = faceFeatures(fuv);
  if (onFace) {
    const ndl = std.dot(std.normalize(input.normal), std.normalize(f.light.xyz));
    const lf = 0.8 + 0.28 * std.smoothstep(-0.3, 0.7, ndl);
    const gate = std.smoothstep(-0.05, 0.15, input.face.z);
    outc = d.vec4f(std.mix(outc.xyz, feat.xyz.mul(lf), feat.w * gate), 1);
  }
  return outputColor(outc);
});

// ── procedural cloth ──────────────────────────────────────────────────────────────────────────────────────────────
export const Cloth = d.struct({
  base: d.vec4f,
  trim: d.vec4f,
  accent: d.vec4f,
  /** x pattern (0 plain, 1 cross, 2 lozenge, 3 mi-parti, 4 metal, 5 mail, 6 ermine, 7 velvet, 8 rope), y trim width, z folds, w painted tension kind (1 torso, 2 drape) */
  params: d.vec4f,
  /** pattern frame: torso centre (rest) + torso tension radius for kind 1 */
  center: d.vec4f,
});
export const clothLayout = tgpu.bindGroupLayout({ cloth: { uniform: Cloth } });

/** A single short tapered fold with a quiet lifted edge; not a repeating noise
 * field. Coordinates are in the unposed garment, so a walk bends the drawing. */
const tensionFold = (p:d.v2f, from:d.v2f, to:d.v2f, bow:number, footprint:number):d.v2f => {
  'use gpu';
  const axis=to.sub(from);
  const length=std.length(axis);
  const along=std.dot(p.sub(from),axis)/(length*length);
  const t=std.clamp(along,0.0,1.0);
  const across=std.dot(p.sub(from),d.vec2f(-axis.y,axis.x).div(length))-bow*4.0*t*(1.0-t);
  const aa=std.max(0.012,footprint*.36);
  const width=0.026*(1.0-t*.78);
  const ends=std.smoothstep(-.06,.035,along)*(1.0-std.smoothstep(.78,1.04,along));
  const ink=(1.0-std.smoothstep(width-aa,width+aa,std.abs(across)))*ends;
  const light=(1.0-std.smoothstep(.018-aa,.018+aa,std.abs(across-.065)))*ends;
  return d.vec2f(ink,light);
};

export const clothFragment = tgpu.fragmentFn({
  in: { world: d.vec3f, normal: d.vec3f, uv: d.vec2f, owner: d.interpolate('flat', d.u32), face: d.vec4f, position: d.builtin.position },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const c = clothLayout.$.cloth;
  const rp = input.face.xyz; // rest position
  const edgeDist = input.face.w;
  const q = rp.sub(c.center.xyz);
  let col = c.base.xyz;
  const pattern = c.params.x;
  let metal = d.f32(0);
  // heraldic cross on the front (rest-space, so it runs on from the chest onto the hanging panel)
  if (pattern > 0.5 && pattern < 1.5) {
    const front = q.z > -0.02;
    const vbar = std.abs(q.x) < 0.024 && q.y < 0.1 && q.y > -0.42;
    const hbar = std.abs(q.y - 0.02) < 0.024 && std.abs(q.x) < 0.085;
    if (front && (vbar || hbar)) {
      col = c.accent.xyz;
    }
  }
  // lozenge diaper with gold knots, wrapped around the torso axis
  if (pattern > 1.5 && pattern < 2.5) {
    const ang = std.atan2(q.z, q.x) * 0.11;
    const g = d.vec2f(ang * 26.0, rp.y * 26.0);
    const cell = std.fract(d.vec2f(g.x + g.y, g.x - g.y).mul(0.5));
    const line = std.min(std.min(cell.x, 1.0 - cell.x), std.min(cell.y, 1.0 - cell.y));
    col = std.mix(col, col.mul(0.72), 1.0 - std.smoothstep(0.02, 0.06, line));
    const knot = std.length(cell.sub(0.5));
    col = std.mix(col, c.trim.xyz, 1.0 - std.smoothstep(0.1, 0.15, knot));
  }
  // mi-parti: left / right halves
  if (pattern > 2.5 && pattern < 3.5) {
    col = std.select(c.base.xyz, c.accent.xyz, q.x > 0.0);
  }
  if (pattern > 3.5 && pattern < 4.5) {
    metal = d.f32(1);
  }
  // Local sleeve/legging coordinates avoid projecting every ring around the
  // torso. The footprint is taken before periodic wrapping, including at the
  // cylindrical seam, then unresolved links converge to their average metal.
  if (pattern > 4.5 && pattern < 5.5) {
    const radius = std.max(0.008, std.length(rp.xz));
    const around = (std.atan2(rp.z, rp.x) / 6.2831853 + 0.5) * input.uv.x;
    const down = rp.y / 0.0145;
    const footprint = std.max(std.length(std.fwidth(rp.xz)) / radius * input.uv.x / 6.2831853, std.fwidth(down));
    const row = std.floor(down);
    const cell = std.fract(d.vec2f(around + row * 0.5, down)).sub(0.5);
    const slant = std.select(d.f32(-0.13), d.f32(0.13), std.fract(row * 0.5) > 0.25);
    const ring = std.abs(std.length(d.vec2f(cell.x + cell.y * slant, cell.y * 0.75)) - 0.34);
    const aa = std.max(0.018, footprint * 0.45);
    const link = 1.0 - std.smoothstep(0.065 - aa, 0.065 + aa, ring);
    const visible = 1.0 - std.smoothstep(0.34, 0.8, footprint);
    const linked = col.mul(0.73 + link * 0.33);
    col = std.mix(col.mul(0.82), linked, visible);
    metal = d.f32(0.45);
  }
  // Ermine belongs to the pale collar only. Tapered tails with three little
  // lobes read as a fur motif instead of rows of rectangular holes.
  if (pattern > 5.5 && pattern < 6.5) {
    const eg = d.vec2f(std.atan2(q.z, q.x) * 0.1 * 30.0 + std.floor(rp.y * 18.0) * 0.5, rp.y * 18.0);
    const cell = std.fract(eg).sub(d.vec2f(0.5, 0.5));
    const stem = std.max(std.abs(cell.x) - (0.025 + std.max(0.0, cell.y) * 0.16), std.abs(cell.y - 0.025) - 0.2);
    const lobes = std.min(std.length(cell.sub(d.vec2f(-0.095, -0.18))), std.min(std.length(cell.sub(d.vec2f(0, -0.25))), std.length(cell.sub(d.vec2f(0.095, -0.18))))) - 0.045;
    const aa = std.max(0.018, std.max(std.fwidth(eg.x), std.fwidth(eg.y)) * 0.3);
    col = std.mix(col, c.accent.xyz, 1.0 - std.smoothstep(-aa, aa, std.min(stem, lobes)));
  }
  // A restrained velvet field leaves the sovereign's silhouette quiet; the
  // ivory edge and one woven gold line supply the decoration.
  if (pattern > 6.5 && pattern < 7.5) {
    col = col.mul(0.96 + 0.035 * std.sin(rp.y * 23.0 + std.sin(rp.x * 15.0)));
    const embroidery = 1.0 - std.smoothstep(0.0008, 0.0022, std.abs(edgeDist - c.params.y * 1.55));
    col = std.mix(col, c.accent.xyz, embroidery * 0.8);
  }
  // The rope's existing band UVs run around the waist and across its width.
  // Broad round shading survives mobile size; diagonal strands fade cleanly.
  if (pattern > 7.5 && pattern < 8.5) {
    const phase = input.uv.x * 28.0 + input.uv.y * 1.25;
    const strandVisibility = 1.0 - std.smoothstep(0.28, 0.85, std.fwidth(phase));
    const twist = (0.5 + 0.5 * std.cos(phase * 6.2831853)) * strandVisibility + 0.5 * (1.0 - strandVisibility);
    const round = 0.6 + 0.4 * std.sin(std.clamp(input.uv.y, 0.0, 1.0) * 3.1415927);
    col = std.mix(c.accent.xyz, c.base.xyz, 0.45 + twist * 0.55).mul(round);
  }
  // trims along every free edge (geodesic distance from the shell / drape boundary) with an inked inner line
  const tw = c.params.y;
  if (tw > 0.0 && edgeDist < tw) {
    col = c.trim.xyz;
  }
  col = std.mix(col, d.vec3f(0.15, 0.1, 0.07), (1.0 - std.smoothstep(0.0, 0.0025, std.abs(edgeDist - tw))) * std.select(d.f32(0), d.f32(0.75), tw > 0.0));
  // Dyed linen: broad pigment variation remains visible at game scale, while
  // fine woven fibres fade below a pixel instead of sparkling in motion.
  const fabric = (1.0 - std.smoothstep(0.1, 0.5, metal)) * std.select(d.f32(1), d.f32(0.28), pattern > 6.5);
  const warp = (rp.x + rp.z * 0.57) * 680.0;
  const weft = rp.y * 740.0 + std.sin(rp.x * 47.0) * 0.6;
  const fibreVisibility = 1.0 - std.smoothstep(0.7, 2.4, std.max(std.fwidth(warp), std.fwidth(weft)));
  const fibres = (std.sin(warp) * 0.025 + std.sin(weft) * 0.022) * fibreVisibility;
  const dye = std.sin(rp.x * 53.0 + rp.z * 37.0) * std.sin(rp.y * 81.0 + std.sin(rp.x * 22.0)) * 0.04;
  col = col.mul(1.0 + (fibres + dye) * fabric);
  const stitchLine = 1.0 - std.smoothstep(0.001, 0.0028, std.abs(edgeDist - tw * 0.52));
  const stitchPhase = (rp.x + rp.z * 0.71 + rp.y * 0.43) * 390.0;
  const stitches = std.smoothstep(0.45, 0.7, std.sin(stitchPhase)) * stitchLine * (1.0 - std.smoothstep(0.8, 2.6, std.fwidth(stitchPhase)));
  col = std.mix(col, c.trim.xyz.mul(1.18), stitches * 0.48 * fabric * std.select(d.f32(0), d.f32(1), tw > 0.004));
  // folds on drapes: soft value ripples that deepen toward the hem
  const folds = c.params.z;
  const ripple = 0.5 + 0.5 * std.cos(input.uv.x * folds * 6.2831853);
  col = col.mul(1.0 - 0.16 * ripple * std.smoothstep(0.1, 1.0, input.uv.y) * std.select(d.f32(0), d.f32(1), folds > 0.5));
  // Deliberate seam-directed marks: shoulders slope toward the chest, belt
  // gathers point upward, and hanging panels open into unequal longer folds.
  // The adapter tags only these garments; hats, cuffs, fur and props stay clean.
  const painted=std.smoothstep(.75,1.0,frameLayout.$.frame.style.z);
  const radius=std.max(.06,c.center.w);
  const tensionP=d.vec2f(std.abs(q.x)/radius,q.y/radius+std.select(d.f32(0),d.f32(.055),q.x<0));
  const torsoFootprint=std.length(std.fwidth(q.xy.div(radius)));
  const shoulder=tensionFold(tensionP,d.vec2f(.83,.01),d.vec2f(.34,-.60),d.f32(.035),torsoFootprint);
  const underarm=tensionFold(tensionP,d.vec2f(1.02,-.44),d.vec2f(.58,-.72),d.f32(-.02),torsoFootprint);
  const waist=tensionFold(tensionP,d.vec2f(.28,-1.25),d.vec2f(.42,-.68),d.f32(.03),torsoFootprint);
  const torsoGate=std.smoothstep(.15,.45,std.abs(q.z)/radius)*std.select(d.f32(0),d.f32(1),c.params.w>.5&&c.params.w<1.5);
  const torsoInk=std.max(shoulder.x,std.max(underarm.x,waist.x))*torsoGate;
  const torsoLight=std.max(shoulder.y,std.max(underarm.y,waist.y))*torsoGate;
  const drapePhase=input.uv.x*7.0;
  const drapeCell=std.floor(drapePhase);
  const drapeSeed=hash21(d.vec2f(drapeCell,7.3));
  const drapeP=d.vec2f(std.fract(drapePhase),input.uv.y);
  const drapeFootprint=std.length(std.fwidth(d.vec2f(drapePhase,input.uv.y)));
  const drape=tensionFold(drapeP,d.vec2f(.46,.03),d.vec2f(.35+drapeSeed*.3,.48+drapeSeed*.44),(.5-drapeSeed)*.15,drapeFootprint);
  const drapeGate=std.select(d.f32(0),d.f32(1),c.params.w>1.5)*std.smoothstep(.10,.23,drapeSeed);
  const clearTrim=std.smoothstep(tw+.002,tw+.01,edgeDist);
  const printStrength=painted*clearTrim*std.select(d.f32(.55),d.f32(.40),pattern>1.5);
  col=std.mix(col,col.mul(.26).add(d.vec3f(.027,.018,.009)),std.max(torsoInk,drape.x*drapeGate)*printStrength);
  col=std.mix(col,col.mul(1.14).add(d.vec3f(.04,.034,.024)),std.max(torsoLight,drape.y*drapeGate)*printStrength*.32);
  return outputColor(stylize(toLinear(col), input.world, input.normal, rp, metal));
});

// ── inverted-hull ink outline ───────────────────────────────────────────────────────────────────────────────────
export const hullVertex = tgpu.vertexFn({
  in: { position: d.vec3f, normal: d.vec3f, uv: d.vec2f, joints: d.vec4u, weights: d.vec4f, aux: d.vec4f },
  out: { position: d.builtin.position, face: d.vec4f },
})((input) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const local = skinned(d.vec4f(input.position, 1), input.joints, input.weights);
  const world = f.model.mul(d.vec4f(local.xyz, 1));
  const ln = skinned(d.vec4f(input.normal, 0), input.joints, input.weights);
  const n = std.normalize(f.model.mul(d.vec4f(ln.xyz, 0)).xyz);
  const clip = f.viewProj.mul(world);
  const clipN = f.viewProj.mul(d.vec4f(n, 0));
  const dir = std.normalize(clipN.xy.add(d.vec2f(0.00001, 0)));
  // constant pixel width: offset in NDC scaled by w
  // Front-face auxiliary data is marked 1; cloth stores its small rest-space z
  // here instead. Do not expand internal socket/whisker backfaces across the
  // reconstructed eyes. The outside head silhouette still receives its ink.
  const faceRadius = std.length(d.vec2f(input.aux.x / 1.45, (input.aux.y + 0.15) / 1.15));
  const faceInk = std.select(d.f32(1), std.smoothstep(0.9, 1.2, faceRadius), input.aux.z > 0.9);
  const pageInk = std.smoothstep(0.75, 1.0, f.style.z);
  const penPressure = 1.0 + pageInk * (0.12 + 0.08 * std.sin(input.position.y * 31.0 + input.position.x * 13.0));
  const offset = dir.mul(f.params.y * 2.0 * faceInk * penPressure).mul(d.vec2f(f.viewport.z, f.viewport.w)).mul(clip.w);
  return { position: d.vec4f(clip.xy.add(offset), clip.z + 0.0004 * clip.w, clip.w), face: input.aux };
});

export const hullFragment = tgpu.fragmentFn({ in: { face: d.vec4f }, out: d.vec4f })((input) => {
  'use gpu';
  // Detached whisker ribbons carry their own fine silhouette. Extruding their
  // hull turns the hidden back edges into thick black loops and broken sticks.
  if (input.face.z < -1.5 && input.face.w > -8.5 && input.face.w < -7.5) std.discard();
  const faceRadius = std.length(d.vec2f(input.face.x / 1.5, (input.face.y + 0.15) / 1.35));
  if (input.face.z > 0.9 && faceRadius < 1.1) std.discard();
  const pageInk = std.smoothstep(0.75, 1.0, frameLayout.$.frame.style.z);
  return outputColor(d.vec4f(std.mix(d.vec3f(0.13, 0.08, 0.06), d.vec3f(0.21, 0.135, 0.085), pageInk), 1));
});
